import { format, parseISO, isValid } from 'date-fns';
import { nl } from 'date-fns/locale';
import { MapPin, Video, Trash2, Clock, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CalendarEvent } from '@/hooks/useOutlookCalendar';

interface CalendarEventCardProps {
  event: CalendarEvent;
  onDelete?: (eventId: string) => void;
  compact?: boolean;
}

function safeFormatTime(dateString: string | null | undefined): string {
  if (!dateString) return '--:--';
  
  let date = parseISO(dateString);
  if (!isValid(date)) {
    date = new Date(dateString);
  }
  if (!isValid(date)) return '--:--';
  
  return format(date, 'HH:mm', { locale: nl });
}

export function CalendarEventCard({ event, onDelete, compact = false }: CalendarEventCardProps) {
  if (compact) {
    return (
      <div className="group relative px-2 py-1.5 rounded-md bg-energy-green/10 hover:bg-energy-green/20 
        border-l-2 border-energy-green transition-all duration-200 cursor-pointer">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-energy-green font-medium">
            {safeFormatTime(event.start_time)}
          </span>
          <span className="text-xs text-foreground truncate flex-1">
            {event.subject}
          </span>
          {event.teams_join_url && (
            <Video className="h-3 w-3 text-energy-blue flex-shrink-0" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="group relative p-4 rounded-xl bg-card border border-border 
      hover:shadow-lg hover:border-energy-green/50 transition-all duration-300
      hover:translate-y-[-2px]">
      {/* Gradient accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl 
        bg-gradient-to-b from-energy-green to-energy-blue" />
      
      {/* Content */}
      <div className="pl-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-foreground group-hover:text-energy-green 
              transition-colors truncate">
              {event.subject}
            </h4>
            
            {/* Time badge */}
            <div className="flex items-center gap-2 mt-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md 
                bg-energy-green/10 text-energy-green text-sm font-mono">
                <Clock className="h-3.5 w-3.5" />
                {safeFormatTime(event.start_time)} - {safeFormatTime(event.end_time)}
              </span>
            </div>
          </div>
          
          {onDelete && (
            <Button 
              variant="ghost" 
              size="icon"
              className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8
                hover:bg-destructive/10 hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(event.id);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* Meta info */}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          {event.location && (
            <span className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-energy-orange" />
              <span className="truncate max-w-[200px]">{event.location}</span>
            </span>
          )}
          {event.teams_join_url && (
            <a 
              href={event.teams_join_url} 
              target="_blank" 
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 text-energy-blue hover:underline 
                transition-colors hover:text-energy-blue-light"
            >
              <Video className="h-3.5 w-3.5" />
              Teams Meeting
            </a>
          )}
          {event.attendees && event.attendees.length > 0 && (
            <span className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              {event.attendees.length} deelnemer{event.attendees.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        {/* Body preview */}
        {event.body && (
          <p className="mt-3 text-sm text-muted-foreground line-clamp-2 
            border-t border-border/50 pt-3">
            {event.body}
          </p>
        )}
      </div>
    </div>
  );
}
